import React, { createContext, useMemo, useState, useCallback } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import Global from '../../../../../store/global-selectors';
import { useIntl } from 'react-intl';
import { generateAttachment, generateReport, generateHotkeyRequest, generateBarcodeRequest } from '../../../api/reports';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { requestParametersFromReportParameters } from './request-helper';
import { REPORTS_LINKS, ERROR_TYPE_GENERAL, ERROR_TYPE_HTML_TOO_LARGE, DevExHtmlTooLargeException, BILLING_REPORTS } from '../../constants';
import { EXPORT_PDF, ACTIONS_MAP, RUN_REPORT } from '../report-actions/constants';
import { PARAMETER_NAME_LABEL_PROVIDER_ID, PARAMETER_NAME_SELLER_LABEL_PROVIDER_ID } from '../report-filters/report-parameter-names';
import { generateReportReadyDateTimeAsISOString } from './request-datetime-helper';
import { filterMessages } from '../../messages';
import { useEnableAsyncReportGeneration } from '../../../../../feature-flags/hooks/use-enable-async-report-generation';
import { generateReportAsync } from '../../../../../store/reports/reports.actions';

const getTodayStartAndEnd = (timeZone) => ({
  startDate: DateTime.now().setZone(timeZone).startOf('day').toUTC().toISO(),
  endDate: DateTime.now().setZone(timeZone).endOf('day').toUTC().toISO(),
});

const ReportsContext = createContext({});

const ReportsContextProvider = ({ children, page }) => {
  const intl = useIntl();
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);
  const userSelectedDateFormat = useSelector(Global.selectUserUserDateFormat);
  const todayDates = getTodayStartAndEnd(sellerTimeZone);
  const [startDate, setStartDate] = useState(page === BILLING_REPORTS ? null : todayDates.startDate);
  const [endDate, setEndDate] = useState(page === BILLING_REPORTS ? null : todayDates.endDate);
  const [reportOutput, setReportOutput] = useAsyncSafeState();
  const [isReportGenerationProcessing, setIsReportGenerationProcessing] = useAsyncSafeState(false);
  const [hasReportGenerationError, setHasReportGenerationError] = useAsyncSafeState(false);
  const [errorType, setErrorType] = useAsyncSafeState(ERROR_TYPE_GENERAL);

  const [params, setParams] = useState({});

  const [selectedStoreId, setSelectedStoreId] = useState('');

  const setDateRange = useCallback(({ startRangeDate, endRangeDate }) => {
    setStartDate(startRangeDate);
    setEndDate(endRangeDate);
  }, []);

  const setReportParams = useCallback(({ apiKey, value }) => {
    setParams((prevParams) => ({
      ...prevParams,
      [apiKey]: value,
    }));
  }, []);

  const requestGenerator = useCallback(
    (action) => {
      const utcStartDate = generateReportReadyDateTimeAsISOString(startDate, sellerTimeZone);
      const utcEndDate = generateReportReadyDateTimeAsISOString(endDate, sellerTimeZone);

      const reportType = REPORTS_LINKS[page];
      if (params?.shipDate) {
        params.shipDate = moment(params.shipDate, userSelectedDateFormat);
      }
      const inputRequestParams = {
        page,
        requestBody: {
          format: ACTIONS_MAP[action],
          reportType,
          startDate: utcStartDate,
          endDate: utcEndDate,
          orderFromDate: utcStartDate,
          orderToDate: utcEndDate,
        },
        ...params,
      };
      const request = requestParametersFromReportParameters(inputRequestParams);
      return request;
    },
    [endDate, page, params, sellerTimeZone, startDate, userSelectedDateFormat],
  );

  const handleReportActionSync = useCallback(
    (action) => {
      const requestBody = requestGenerator(action);
      if (action === RUN_REPORT) {
        generateReport(requestBody)
          .then((response) => {
            setIsReportGenerationProcessing(false);
            return setReportOutput(response.data);
          })
          .catch((e) => {
            const firstErrorMessage = e?.response?.data?.Errors[0]?.Message || '';
            const isReportTooLarge = firstErrorMessage.indexOf(DevExHtmlTooLargeException) >= 0;
            if (isReportTooLarge) {
              setErrorType(ERROR_TYPE_HTML_TOO_LARGE);
            }
            setIsReportGenerationProcessing(false);
            setHasReportGenerationError(true);
          });
      } else {
        const apiCall = page === BILLING_REPORTS ? generateReport : generateAttachment;
        apiCall(requestBody)
          .then(() => setIsReportGenerationProcessing(false))
          .catch(() => {
            setIsReportGenerationProcessing(false);
            setHasReportGenerationError(true);
          });
      }
    },
    [requestGenerator, setReportOutput, setIsReportGenerationProcessing, setHasReportGenerationError, setErrorType, page],
  );

  const dispatch = useDispatch();
  const shouldUseAsyncReportGeneration = useEnableAsyncReportGeneration();
  const handleReportAction = useCallback(
    (action) => {
      setReportOutput(undefined);
      setIsReportGenerationProcessing(true);
      setHasReportGenerationError(false);

      if (shouldUseAsyncReportGeneration) {
        const onError = (error) => {
          const firstErrorMessage = error?.Message || '';
          const isReportTooLarge = firstErrorMessage.indexOf(DevExHtmlTooLargeException) >= 0;
          if (isReportTooLarge) {
            setErrorType(ERROR_TYPE_HTML_TOO_LARGE);
          }
          setIsReportGenerationProcessing(false);
          setHasReportGenerationError(true);
        };
        const onGenerationComplete = (response) => {
          setIsReportGenerationProcessing(false);
          if (action === RUN_REPORT) {
            setReportOutput(response.data);
          }
        };
        const requestBody = requestGenerator(action);
        dispatch(generateReportAsync(requestBody, action, onGenerationComplete, onError));
      } else {
        handleReportActionSync(action);
      }
    },
    [
      dispatch,
      requestGenerator,
      setErrorType,
      setReportOutput,
      setIsReportGenerationProcessing,
      setHasReportGenerationError,
      handleReportActionSync,
      shouldUseAsyncReportGeneration,
    ],
  );

  const handleHotkeyRequest = useCallback(
    (reportOutputType) => {
      const requestBody = requestGenerator(reportOutputType);
      setReportOutput(undefined);
      setIsReportGenerationProcessing(true);
      setHasReportGenerationError(false);
      const shouldGenerateAsAttachment = reportOutputType === EXPORT_PDF;
      generateHotkeyRequest(requestBody, shouldGenerateAsAttachment)
        .then((response) => {
          if (!shouldGenerateAsAttachment) {
            return setReportOutput(response.data);
          }
        })
        .catch(() => setHasReportGenerationError(true))
        .finally(() => setIsReportGenerationProcessing(false));
    },
    [requestGenerator, setHasReportGenerationError, setIsReportGenerationProcessing, setReportOutput],
  );

  const handleBarcodeRequest = useCallback(
    (request) => {
      const requestBody = {
        ...requestGenerator(EXPORT_PDF),
        ...request,
      };
      setReportOutput(undefined);
      setIsReportGenerationProcessing(true);
      setHasReportGenerationError(false);
      generateBarcodeRequest(requestBody)
        .catch(() => setHasReportGenerationError(true))
        .finally(() => setIsReportGenerationProcessing(false));
    },
    [requestGenerator, setHasReportGenerationError, setIsReportGenerationProcessing, setReportOutput],
  );

  const getSellerLabelProviderName = useCallback(
    (labelProvider, sellerLabelProvider, index) => {
      let name = sellerLabelProvider.name;
      if (!sellerLabelProvider.name && sellerLabelProvider.accountNumber) {
        name = `${labelProvider.name} - ${sellerLabelProvider.accountNumber}`;
      }

      if (!sellerLabelProvider.name && !sellerLabelProvider.accountNumber && !sellerLabelProvider.isPrimaryAccount) {
        name = `${labelProvider.name} ${index}`;
      }

      if (!sellerLabelProvider.name && !sellerLabelProvider.accountNumber && sellerLabelProvider.isPrimaryAccount) {
        name = labelProvider.name;
      }

      if (sellerLabelProvider.isPrimaryAccount) {
        name = intl.formatMessage(filterMessages.primaryAccount, {
          accountName: name,
        });
      }

      return name;
    },
    [intl],
  );

  const { [PARAMETER_NAME_LABEL_PROVIDER_ID]: labelProviderId } = params;
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const labelProviders = useSelector(Global.selectReferenceDatagetProviders);
  const myAccountsForProvider = useMemo(() => {
    const selectedLabelProvider = labelProviders.find((lp) => lp.labelProviderId === labelProviderId);
    return sellerLabelProviders
      .filter((acct) => acct.labelProviderId === labelProviderId)
      .map((act, index) => ({
        ...act,
        name: getSellerLabelProviderName(selectedLabelProvider, act, index),
        [PARAMETER_NAME_SELLER_LABEL_PROVIDER_ID]: act.sellerLabelProviderId,
      }));
  }, [labelProviders, labelProviderId, sellerLabelProviders, getSellerLabelProviderName]);

  const value = useMemo(
    () => ({
      endDate,
      handleReportAction,
      page,
      selectedStoreId,
      setDateRange,
      setEndDate,
      setReportParams,
      setSelectedStoreId,
      setStartDate,
      startDate,
      labelProviderId,
      myAccountsForProvider,
      reportOutput,
      isReportGenerationProcessing,
      hasReportGenerationError,
      errorType,
      handleHotkeyRequest,
      handleBarcodeRequest,
      requestGenerator,
      params,
    }),
    [
      endDate,
      handleReportAction,
      page,
      selectedStoreId,
      setDateRange,
      setEndDate,
      setReportParams,
      setSelectedStoreId,
      setStartDate,
      startDate,
      labelProviderId,
      myAccountsForProvider,
      reportOutput,
      isReportGenerationProcessing,
      hasReportGenerationError,
      errorType,
      handleHotkeyRequest,
      handleBarcodeRequest,
      requestGenerator,
      params,
    ],
  );

  return <ReportsContext.Provider value={value}>{children}</ReportsContext.Provider>;
};

ReportsContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  page: PropTypes.string.isRequired,
};

export { ReportsContext };

export default ReportsContextProvider;
